<template>
    <a-card :title="$t('screenTitle.chargeViaCard')">
        <a-skeleton :loading="!isReady" active>
            <a-alert type="error" message="Cảnh báo!!!" show-icon class="mb-20">
                <template #description>
                    <h2>Bạn có chắc chắn mình không sử dụng Ngân Hàng/ATM/Mobile Banking/Momo/App Banking,...?</h2>
                    <div class="mb-20">Nạp bằng thẻ cào sẽ bị khấu trừ chiết khấu lên tới 30% (Tức là nạp thẻ
                        100k có thể chỉ nhận được 70k)</div>
                    <!--<router-link :to="{ name: RouteName.PAYMENT_CHARGE_VIA_EBANK }">
                        <a-button type="primary">{{ $t('text.chargeEbankNow') }}</a-button>
                    </router-link>-->
                </template>
            </a-alert>

            <a-alert v-if="!isValidClientAddress" type="warning" message="Bạn không có quyền truy cập trang này" show-icon>
                <template #description>
                    <div>Nếu bạn là một người chơi ở nước ngoài (hiện không ở Việt Nam), hãy liên hệ Fanpage/Admin để nhận
                        trợ giúp về [<strong>Nạp tiền tại nước ngoài</strong>]</div>
                </template>
            </a-alert>

            <a-form v-else ref="form" :model="formModel" @finish="onFinish" :layout="'vertical'" :rules="formRule"
                :scrollToFirstError="true">
                <a-form-item :label="$t('labelText.charge_card_type')" name="type">
                    <a-select v-model:value="formModel.type" :options="Object.keys(cardOptions?.list || {}).map(key => {
                            return {
                                value: key,
                                label: cardOptions?.list[key],
                                disabled: cardOptions?.disabled?.includes(key),
                            };
                        })" />
                </a-form-item>

                <a-form-item :label="$t('labelText.charge_card_price')" name="price">
                    <a-select v-model:value="formModel.price" show-search :options="[10000, 20000, 30000, 50000, 100000, 200000, 300000, 500000, 1000000].map(v => {
                            return {
                                value: v,
                                label: number_format(v),
                            };
                        })" />
                </a-form-item>

                <a-form-item :label="$t('labelText.charge_card_serial')" name="serial">
                    <a-input v-model:value="formModel.serial" />
                </a-form-item>

                <a-form-item :label="$t('labelText.charge_card_code')" name="code">
                    <a-input v-model:value="formModel.code" />
                </a-form-item>

                <a-form-item>
                    <a-button type="primary" html-type="submit" :loading="isLoading">{{ $t('text.charge') }}</a-button>
                </a-form-item>
            </a-form>
        </a-skeleton>

        <a-table v-if="hasManagerPermission" :dataSource="dataSource" :columns="columns" :pagination="pagination" :loading="isLoadingTable" :scroll="{x: '100%'}">
            <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'action'">
                    <a-space>
                        <a-tooltip v-if="record.status !== PaymentStatus.COMPLETED" title="Xem thông tin giao dịch">
                            <a-button type="primary" size="small" @click="checkPaymentConfirm(record)">
                                <template #icon><info-circle-outlined /></template>
                            </a-button>
                        </a-tooltip>

                        <a-tooltip v-if="record.status === PaymentStatus.PROCESSING" title="Huỷ giao dịch">
                            <a-button type="primary" danger size="small" @click="deletePaymentConfirm(record)">
                                <template #icon><DeleteOutlined /></template>
                            </a-button>
                        </a-tooltip>
                    </a-space>
                </template>
            </template>
        </a-table>
    </a-card>
</template>

<script lang="ts">
import { computed, defineComponent, h, onMounted, onUnmounted, reactive, ref, watch, } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { Alert, Button, Card, Form, Input, message, Modal, Select, Skeleton, Space, Table, Tooltip, } from "ant-design-vue";
import { RuleObject } from "ant-design-vue/lib/form";
import { ColumnType, TablePaginationConfig } from "ant-design-vue/lib/table";
import { DeleteOutlined, InfoCircleOutlined, } from "@ant-design/icons-vue";

import { RouteName } from "@/router";
import ApiRequest from "@/utils/ApiRequest";
import HttpRequest from "@/utils/HttpRequest";
import { UserRole } from "@/store/modules/user";
import { number_format } from "@/utils/helpers";
import { UserInfo } from '@/store/modules/user';
import { paymentStatusText, PaymentStatus } from "./HistoryView.vue";

interface FormModel {
    type?: number,
    serial: string,
    code: string,
    price?: number,
}

export default defineComponent({
    components: {
        ACard: Card,
        AAlert: Alert,
        AButton: Button,
        AInput: Input,
        ASelect: Select,
        AForm: Form,
        AFormItem: Form.Item,
        ATable: Table,
        ASpace: Space,
        ATooltip: Tooltip,
        ASkeleton: Skeleton,

        DeleteOutlined,
        InfoCircleOutlined,
    },

    setup() {
        const { t } = useI18n();
        const store = useStore();

        const isLoadingTable = ref(true);
        const dataSource = ref([]);
        const columns = ref<ColumnType[]>([
            {
                title: 'ID',
                dataIndex: 'id',
            },
            {
                title: t('labelText.payment_username'),
                dataIndex: ['account', 'name'],
            },
            {
                title: t('labelText.payment_status'),
                dataIndex: 'status',
                customRender: ({ text }) => paymentStatusText(text),
            },
            {
                title: t('labelText.payment_coin'),
                dataIndex: 'amount',
                customRender: ({ text }) => {
                    return number_format(text);
                },
            },
            {
                title: t('labelText.payment_transaction_info'),
                dataIndex: 'transaction_info',
            },
            {
                title: t('labelText.created_at'),
                dataIndex: "created_at",
            },
            {
                title: t('labelText.updated_at'),
                dataIndex: "updated_at",
            },
            {
                title: t('text.action'),
                key: "action",
            },
        ]);
        const pagination = ref<TablePaginationConfig>({
            current: 1,
        });
        const fetchData = () => {
            isLoadingTable.value = true;

            ApiRequest.get('/payments/charge-card', {
                page: pagination.value.current,
            })
                .then(res => {
                    const resData = res.data;

                    dataSource.value = resData.data;

                    ApiRequest.setPaginate(pagination.value, resData);
                })
                .finally(() => {
                    isLoadingTable.value = false;
                })
        };

        const isReady = ref(false);
        const userInfo = computed<UserInfo>(() => store.getters.user);
        const hasManagerPermission = computed<boolean>(() => store.getters.hasManagerPermission);

        const cardOptions = ref();

        const isValidClientAddress = ref(false);

        let fetchingValid = false;

        const fetchValid = () => {
            if (fetchingValid) {
                return;
            }

            isReady.value = false;
            fetchingValid = true;

            if (!hasManagerPermission.value) {
                // http://www.geoplugin.net/json.gp
                // https://www.cloudflare.com/cdn-cgi/trace
                HttpRequest.get('https://www.cloudflare.com/cdn-cgi/trace')
                    .then(res => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isValidClientAddress.value = res.data.split("\n").filter((v: any) => v.includes("loc="))[0].split("=")[1] === 'VN';
                    })
                    .finally(() => {
                        fetchingValid = false;
                        isReady.value = true;
                    });
            } else {
                isValidClientAddress.value = true;
                fetchingValid = false;
            }
        };

        pagination.value.onChange = (page) => {
            pagination.value.current = page;

            fetchData();
        };

        watch(() => isValidClientAddress.value, (val) => {
            isReady.value = false;
            if (val) {
                ApiRequest.get('/payments/card-info')
                    .then(res => {
                        cardOptions.value = res.data.data;
                    })
                    .finally(() => {
                        isReady.value = true;
                    });
            } else {
                isReady.value = true;
            }
        });

        const form = ref<typeof Form>();

        const formModel = reactive<FormModel>({
            serial: '',
            code: '',
        });

        const formExtraError: Record<keyof FormModel, string> = {
            type: '',
            serial: '',
            code: '',
            price: '',
        };
        const formRule = reactive<Record<keyof FormModel, RuleObject[]>>({
            type: [
                {
                    required: true,
                    trigger: 'change',
                },
                {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    validator: (_rule: any, _value, callback) => callback(formExtraError[_rule.field as keyof FormModel] || undefined),
                },
            ],
            serial: [
                {
                    required: true,
                    trigger: ['blur', 'change'],
                },
                {
                    pattern: /^[\w]*$/,
                    message: 'Không được chứa kí tự đặc biệt và khoảng trắng',
                    trigger: ['blur', 'change'],
                },
                {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    validator: (_rule: any, _value, callback) => callback(formExtraError[_rule.field as keyof FormModel] || undefined),
                },
            ],
            code: [
                {
                    required: true,
                    trigger: ['blur', 'change'],
                },
                {
                    pattern: /^[\w]*$/,
                    message: 'Không được chứa kí tự đặc biệt và khoảng trắng',
                    trigger: ['blur', 'change'],
                },
                {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    validator: (_rule: any, _value, callback) => callback(formExtraError[_rule.field as keyof FormModel] || undefined),
                },
            ],
            price: [
                {
                    required: true,
                    trigger: 'change',
                },
                {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    validator: (_rule: any, _value, callback) => callback(formExtraError[_rule.field as keyof FormModel] || undefined),
                },
            ],
        });

        const isLoading = ref(false);

        const onFinish = (values: FormModel) => {
            isLoading.value = true;

            ApiRequest.post('/payments/charge-card', values)
                .then(() => {
                    message.success(t('message.paymentChargeCardSuccess'));

                    form.value?.resetFields();
                })
                .catch(err => {
                    ApiRequest.handleError(err, formExtraError, form.value);
                    if (err.response?.data?.data?.message) {
                        Modal.error({
                            content: err.response.data.data.message,
                        });
                    }
                })
                .finally(() => {
                    isLoading.value = false;
                });
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const deletePaymentConfirm = (record: any) => {
            Modal.confirm({
                title: t('message.deleteConfirmContent'),
                content: `#${record.id}: ${number_format(record.amount)} - ${record.transaction_info}`,
                autoFocusButton: 'cancel',
                maskClosable: true,
                onOk: async () => {
                    return ApiRequest.del('/payments/charge-card/' + record.id).then(() => {
                        message.success(t('message.deleteSuccess'));
                    })
                    .finally(() => {
                        fetchData();
                    });
                },
            });
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const checkPaymentConfirm = (record: any) => {
            isLoadingTable.value = true;
            ApiRequest.get('/payments/charge-card/' + record.id).then(res => {
                const resData = res.data.data.data;
                Modal.info({
                    title: `#${record.id}: ${number_format(record.amount)} - ${record.transaction_info}`,
                    content: h('div', Object.keys(resData).map(key => {
                            return h('div', `${key}: ${resData[key]}`);
                        })
                    ),
                    maskClosable: false,
                });
            })
                .finally(() => {
                    fetchData();
                });
        };

        const fetchDataInterval = setInterval(() => {
            if (hasManagerPermission.value && !isLoadingTable.value) {
                fetchData();
            }
        }, 60000);

        onMounted(() => {
            if (userInfo.value.id) {
                fetchValid();
            }

            if (hasManagerPermission.value) {
                fetchData();
            }
        });

        onUnmounted(() => {
            clearInterval(fetchDataInterval);
        });

        watch(() => userInfo.value.id, (val) => {
            if (val) {
                fetchValid();
            }

            if (hasManagerPermission.value) {
                fetchData();
            }
        });

        return {
            isLoading,
            isReady,

            hasManagerPermission,

            number_format,

            fetchValid,
            isValidClientAddress,

            userInfo,

            formModel,
            formRule,
            formExtraError,
            form,
            onFinish,

            cardOptions,

            dataSource,
            columns,
            pagination,
            isLoadingTable,
            deletePaymentConfirm,
            checkPaymentConfirm,

            PaymentStatus,

            RouteName,

            UserRole,
        };
    },
})
</script>

<style scoped>

</style>
